import React, { useEffect, useRef } from 'react';

// Snowflake Cursor Effect Component
const SnowflakeCursor = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    let canvas = canvasRef.current;
    let context = canvas.getContext("2d");
    let particles = [];
    let animationFrame;
    let canvImages = [];
    let width = window.innerWidth;
    let height = window.innerHeight;
    let cursor = { x: width / 2, y: width / 2 };

    // Define Snowflake emojis
    const possibleEmoji = ["❄️","🛞","⭐","🌟"];
    
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    );

    // Re-initialise or destroy the cursor when the prefers-reduced-motion setting changes
    prefersReducedMotion.onchange = () => {
      if (prefersReducedMotion.matches) {
        destroy();
      } else {
        init();
      }
    };

    function init() {
      if (prefersReducedMotion.matches) {
        console.log(
          "This browser has prefers reduced motion turned on, so the cursor did not init"
        );
        return false;
      }

      canvas.style.position = "fixed";
      canvas.style.top = "0";
      canvas.style.left = "0";
      canvas.style.pointerEvents = "none";
      canvas.style.zIndex = "1000"; // Ensure the canvas is on top
      canvas.width = width;
      canvas.height = height;

      context.font = "12px serif";
      context.textBaseline = "middle";
      context.textAlign = "center";

      possibleEmoji.forEach((emoji) => {
        let measurements = context.measureText(emoji);
        let bgCanvas = document.createElement("canvas");
        let bgContext = bgCanvas.getContext("2d");

        bgCanvas.width = measurements.width;
        bgCanvas.height = measurements.actualBoundingBoxAscent * 2;

        bgContext.textAlign = "center";
        bgContext.font = "12px serif";
        bgContext.textBaseline = "middle";
        bgContext.fillText(
          emoji,
          bgCanvas.width / 2,
          measurements.actualBoundingBoxAscent
        );

        canvImages.push(bgCanvas);
      });

      bindEvents();
      loop();
    }

    function bindEvents() {
      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("touchmove", onTouchMove, { passive: true });
      document.addEventListener("touchstart", onTouchMove, { passive: true });
      window.addEventListener("resize", onWindowResize);
    }

    function onWindowResize(e) {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width;
      canvas.height = height;
    }

    let lastMouseMoveTime = 0;
    const mouseMoveThrottle = 50; // Throttle time in milliseconds

    function onTouchMove(e) {
      const now = Date.now();
      if (now - lastMouseMoveTime > mouseMoveThrottle) {
        lastMouseMoveTime = now;

        if (e.touches.length > 0) {
          for (let i = 0; i < e.touches.length; i++) {
            addParticle(
              e.touches[i].clientX,
              e.touches[i].clientY,
              canvImages[Math.floor(Math.random() * canvImages.length)]
            );
          }
        }
      }
    }

    function onMouseMove(e) {
      const now = Date.now();
      if (now - lastMouseMoveTime > mouseMoveThrottle) {
        lastMouseMoveTime = now;

        cursor.x = e.clientX;
        cursor.y = e.clientY;

        addParticle(
          cursor.x,
          cursor.y,
          canvImages[Math.floor(Math.random() * possibleEmoji.length)]
        );
      }
    }

    function addParticle(x, y, img) {
      particles.push(new Particle(x, y, img));
    }

    function updateParticles() {
      if (particles.length === 0) {
        return;
      }

      context.clearRect(0, 0, width, height);

      for (let i = 0; i < particles.length; i++) {
        particles[i].update(context);
      }

      for (let i = particles.length - 1; i >= 0; i--) {
        if (particles[i].lifeSpan < 0) {
          particles.splice(i, 1);
        }
      }

      if (particles.length === 0) {
        context.clearRect(0, 0, width, height);
      }
    }

    function loop() {
      updateParticles();
      animationFrame = requestAnimationFrame(loop);
    }

    function destroy() {
      cancelAnimationFrame(animationFrame);
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("touchstart", onTouchMove);
      window.removeEventListener("resize", onWindowResize);
    }

    function Particle(x, y, canvasItem) {
      const lifeSpan = Math.floor(Math.random() * 60 + 120); // Increased lifespan
      this.initialLifeSpan = lifeSpan;
      this.lifeSpan = lifeSpan; // ms
      this.velocity = {
        x: (Math.random() < 0.5 ? -1 : 1) * (Math.random() / 4), // Reduced velocity
        y: 0.5 + Math.random() / 2, // Reduced vertical speed
      };
      this.position = { x: x, y: y };
      this.canv = canvasItem;

      this.update = function (context) {
        this.position.x += this.velocity.x;
        this.position.y += this.velocity.y;
        this.lifeSpan--;

        this.velocity.x += ((Math.random() < 0.5 ? -1 : 1) * 1) / 75; // Reduced speed variation
        this.velocity.y -= Math.random() / 300;

        const scale = Math.max(this.lifeSpan / this.initialLifeSpan, 0);

        const degrees = 2 * this.lifeSpan;
        const radians = degrees * 0.0174533;

        context.translate(this.position.x, this.position.y);
        context.rotate(radians);

        context.drawImage(
          this.canv,
          (-this.canv.width / 2) * scale,
          -this.canv.height / 2,
          this.canv.width * scale,
          this.canv.height * scale
        );

        context.rotate(-radians);
        context.translate(-this.position.x, -this.position.y);
      };
    }

    init();

    return () => {
      destroy();
    };
  }, []);

  return <canvas ref={canvasRef} />;
};

export default SnowflakeCursor;
